import { Box } from "@material-ui/core";
import { isNil, mapObjIndexed } from "ramda";
import { useCallback, useEffect, useRef, useState } from "react";
import { Title, useNotify, useRedirect } from "react-admin";

import Loader from "../../../components/Loader";
import useDisabledOnLoading from "../../../hooks/useDisabledOnLoading";
import { GenericObject, WeekDay } from "../../../types/generic.types";
import { AppResource } from "../../../types/resource.types";
import { coachDocRef, coachAvailabilitiesRangesSubCollRef } from "../../../utils/referencies";
import { applyCreateTimestamp } from "../../../utils/timestamp";
import { CellManageMode, IRangeRef, SlotsMap } from "./types";
import { disableSubmitBtn, initialSlots } from "./utils";
import { checkReservedSlots, getCoach, getSlotsInRange } from "./utils/queries";
import AvailabilitySlots from "./AvailabilitySlots";
import AvailabilityTab from "./AvailabilityTab";
import AvailabilityToolBar from "./AvailabilityToolBar";
import useCurrentUser from "../../../hooks/useCurrentUser";

const CoachAvailability = () => {
  const { user } = useCurrentUser();

  const notify = useNotify();
  const redirect = useRedirect();

  const [coach, setCoach] = useState<GenericObject | null>(null);
  const [currentDay, setCurrentDay] = useState<WeekDay>(WeekDay.saturday);
  const [allSlots, setAllSlots] = useState<SlotsMap>(new Map(initialSlots));
  const [getCoachLoading, setGetCoachLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [time, setTime] = useState(3600);
  const [hasReservedSlots, setHasReservedSlots] = useState(false);

  const { disabledStyles } = useDisabledOnLoading(submitLoading);

  const rangeRef = useRef<IRangeRef>({
    startDate: new Date(),
    endDate: new Date(),
  });

  const handleSelectDay = (day: WeekDay) => {
    if (currentDay !== day) setCurrentDay(day);
  };

  const handleSelectedSlots = (slots: Set<number>) => {
    setAllSlots((prev) => new Map(prev.set(currentDay, slots)));
  };

  const handleSubmit = useCallback(
    async (time: number) => {
      if (rangeRef.current.startDate && rangeRef.current.endDate) {
        const days = Object.fromEntries(allSlots.entries());
        const preparedDays = mapObjIndexed((value: Set<number>) => [...value])(days);
        const data = {
          ...preparedDays,
          ...rangeRef.current,
          append: false,
          overwrite: true,
          timeZoneOffset: new Date().getTimezoneOffset(),
          duration: time,
        };
        console.log(applyCreateTimestamp(data));

        setSubmitLoading(true);
        try {
          const hasReservedSlots = await checkReservedSlots(
            getSlotsInRange(user.id!, rangeRef.current.startDate, rangeRef.current.endDate),
            CellManageMode.overwrite
          );
          if (hasReservedSlots) {
            notify("Overwrite failed, this range has some reserved slots", "error");
            setSubmitLoading(false);
            return;
          }
        } catch (ex: any) {
          console.log(ex.message);
          setSubmitLoading(false);
          return;
        }

        coachAvailabilitiesRangesSubCollRef(user.id!)
          .add(applyCreateTimestamp(data))
          .then(() => {
            setAllSlots(new Map(initialSlots));
            notify("Slots have been updated", "success");
            redirect(AppResource.coachSlotsCalendar);
          })
          .catch((ex: any) => {
            console.log(ex.message);
            notify("Error on editing slots", "error");
          })
          .finally(() => {
            setSubmitLoading(false);
          });
      }
    },
    [user.id!, allSlots]
  );

  const isDayHasSlots = useCallback(
    (day: WeekDay): boolean => {
      const slots = allSlots.get(day);
      if (!isNil(slots)) return slots.size > 0;
      return false;
    },
    [allSlots]
  );

  useEffect(() => {
    setGetCoachLoading(true);
    const getCoachSubscription = getCoach(coachDocRef(user.id!)).subscribe({
      next(data) {
        setCoach(data);
        setGetCoachLoading(false);
      },
    });

    return () => {
      getCoachSubscription.unsubscribe();
    };
  }, [user.id!]);

  return (
    <>
      <Title title="Coach Availability Slots" />
      <Loader loading={getCoachLoading} height="80vh" size={26}>
        {coach ? (
          <Box style={disabledStyles}>
            <AvailabilityToolBar
              rangeRef={rangeRef}
              onSubmit={handleSubmit}
              loading={submitLoading}
              disableSubmitBtn={disableSubmitBtn(allSlots)}
              onSelectTime={(time: number) => setTime(time)}
              defaultMinSlot={user.defaultSlotTime}
              onHasReservedSlots={(hasReservedSlots: boolean) => setHasReservedSlots(hasReservedSlots)}
            />
            {!hasReservedSlots && (
              <>
                <AvailabilityTab selectedDay={currentDay} setSelectedId={handleSelectDay} hasSlots={isDayHasSlots} />
                <AvailabilitySlots
                  initialDaySlots={allSlots.get(currentDay)}
                  minSlot={time}
                  getSlots={handleSelectedSlots}
                  currentDayName={currentDay}
                />
              </>
            )}
          </Box>
        ) : null}
      </Loader>
    </>
  );
};

export default CoachAvailability;
